import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import AuthService from '../../services/auth';
import RootContext from '../../services/context-states/root-context';

import { callApi } from '../../api/helpers';
import { userApi } from '../../api/openapi-axios-client';

import { Model } from 'survey-core';
import 'survey-core/defaultV2.min.css';
import { Survey } from 'survey-react-ui';
import surveyJson from './survey-configs/registration-survey.json';
import { returnTheme } from './survey-configs/Theme';
import { MessageResponse } from '../../types';

const RegistrationSurvey: React.FC = () => {
    const { userContext, messageContext } = useContext(RootContext);
    const { currentUser } = userContext;
    const { setError } = messageContext;

    const [showSurvey, setShowSurvey] = useState<boolean>(false);
    
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const isInvite = searchParams.get('isInvite');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const survey = useMemo(() => {
        const model = new Model(surveyJson);
        // @ts-expect-error - Ignoring type mismatch with backgroundImageFit property
        model.applyTheme(returnTheme());
        return model;
    }, []);

    useEffect(() => {
        const handler = async (sender: any) => {
            try {
                await callApi<MessageResponse>(() => userApi.userSurveys.createSurvey({
                    userId: currentUser?.id || '',
                    key: 'registration-survey',
                    response: JSON.stringify(sender.data)
                }));

                navigate('/home?welcome=true');
            } catch (error) {
                console.error('Error saving registration survey:', error);
                setError('We could not save your responses. Please try again.');
            }
        };

        survey.onComplete.add(handler);
        return () => { survey.onComplete.remove(handler); };
    }, [survey, currentUser?.id, navigate, setError]);

    const welcomeBanner = (): React.ReactElement => {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'center' : 'flex-start',
                borderRadius: '5px',
                borderColor: theme.palette.container.border,
                backgroundColor: theme.palette.container.background,
                borderWidth: '1px',
                borderStyle: 'solid',
                padding: isMobile ? 2 : 4,
                gap: isMobile ? 2 : 3,
                maxWidth: isMobile ? '90vw' : '800px',
                width: '100%'
            }}>
                <Typography sx={{fontSize: isMobile ? 36 : 48, textAlign: 'center'}}>{String.fromCodePoint(0x1F44B)}</Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25, textAlign: isMobile ? 'center' : 'left'}}>
                    <Typography sx={{fontSize: isMobile ? 16 : 18}}>Welcome to Future Gen's CareerAtlas!</Typography>
                    <Typography sx={{fontSize: isMobile ? 16 : 18}}>Before we get started, we'd love to ask you a few questions to get to know you.</Typography>
                    <Button
                        variant='contained'
                        color='primary'
                        type='submit'
                        sx={{
                            width: isMobile ? '100%' : 150,
                            marginTop: 3,
                            alignSelf: isMobile ? 'stretch' : 'flex-start'
                        }}
                        onClick={() => {
                            if(!currentUser || !currentUser.id || !AuthService.getCurrentUser()?.token) {
                                navigate('/');
                                return;
                            }
                            setShowSurvey(true)
                        }}
                        aria-label="Submit registration survey">
                        Start Survey
                    </Button>
                </Box>
            </Box>
        )
    }

    return (
        <Container
            maxWidth={isMobile ? false : "lg"}
            disableGutters={isMobile}
            sx={{
                px: { xs: 2, sm: 3, md: 3 },
                py: { xs: 2, sm: 3, md: 4 },
                width: '100%'
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: isMobile ? 4 : 12,
                padding: isMobile ? 1 : 2,
                minHeight: isMobile ? 'calc(100vh - 200px)' : 'auto'
            }}>
                {!showSurvey ? welcomeBanner() :
                    <Box sx={{
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '800px',
                        '& .sv-root-modern': {
                            padding: isMobile ? '8px' : '16px'
                        }
                    }}>
                        <Survey model={survey} />
                    </Box>
                }
            </Box>
        </Container>
    );
};

export default RegistrationSurvey;